import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Drawer, Typography, Stack, Chip, IconButton, Button, Divider, Tabs, Tab, CircularProgress, Avatar,
} from '@mui/material';
import {
  Close as CloseIcon, Apple, Android, OpenInNew, CheckCircle, ErrorOutline, HourglassEmpty, BugReportOutlined,
} from '@mui/icons-material';
import axios from 'axios';

// Minimal build shape this drawer needs — both the Home activity feed
// (PipelineData) and the Error Logs page satisfy it.
export interface BuildErrorTarget {
  id: number | string;
  pipelineId?: number | string;
  composerHost?: string;
  appName?: string;
  version?: string;
  platform?: string;        // osSpecification / platform
  status?: string;          // pipelineStatus / status
  submitter?: string;
  pipelineUrl?: string;
}

const statusMeta = (status: string) => {
  const s = (status || '').toLowerCase();
  if (/success/.test(s)) return { label: 'Success', color: '#16a34a', bg: 'rgba(22, 163, 74, 0.12)', icon: <CheckCircle sx={{ fontSize: 13 }} /> };
  if (/fail|cancel/.test(s)) return { label: /cancel/.test(s) ? 'Cancelled' : 'Failed', color: '#b91c1c', bg: 'rgba(220, 38, 38, 0.12)', icon: <ErrorOutline sx={{ fontSize: 13 }} /> };
  if (/queue|running|pending/.test(s)) return { label: 'Running', color: '#1d4ed8', bg: 'rgba(59, 130, 246, 0.12)', icon: <HourglassEmpty sx={{ fontSize: 13 }} /> };
  return { label: status || 'N/A', color: '#475569', bg: 'rgba(15, 23, 42, 0.06)', icon: null };
};

const phaseColor = (s: string) => {
  const ss = (s || '').toLowerCase();
  if (/success/.test(ss)) return '#16a34a';
  if (/fail|cancel|error/.test(ss)) return '#dc2626';
  if (/run|pending|queue/.test(ss)) return '#2563eb';
  return '#94a3b8';
};

interface Phase { phase: string; status: string; errorMessage?: string | null; errorCode?: string | null; gitlabJobUrl?: string | null; startedAt?: string | null; finishedAt?: string | null; }

interface GitlabJob { id: number; name: string; status: string; web_url?: string; }

const jobStatusColor = (s: string) => {
  const ss = (s || '').toLowerCase();
  if (/success/.test(ss)) return '#16a34a';
  if (/fail/.test(ss)) return '#dc2626';
  if (/cancel/.test(ss)) return '#f59e0b';
  if (/run|pending|created/.test(ss)) return '#2563eb';
  return '#94a3b8';
};

interface BuildErrorDrawerProps {
  build: BuildErrorTarget | null;
  onClose: () => void;
}

// Per-build drawer showing the core failure reason (from build phases) and the
// complete GitLab job log (fetched lazily via the worker), so operators can
// triage straight from the Home activity feed without leaving the page.
export const BuildErrorDrawer: React.FC<BuildErrorDrawerProps> = ({ build, onClose }) => {
  const [tab, setTab] = useState(0);
  const [phases, setPhases] = useState<Phase[] | null>(null);
  const [reason, setReason] = useState<string>('');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [trace, setTrace] = useState<string>('');
  const [traceMeta, setTraceMeta] = useState<{ truncated: boolean } | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceError, setTraceError] = useState<string | null>(null);
  // GitLab pipelines run several jobs (npm build, fastlane, upload, …); the
  // trace endpoint returns the full job list plus the currently-shown job so we
  // can let the operator switch between them instead of dumping one blob.
  const [jobs, setJobs] = useState<GitlabJob[]>([]);
  const [activeJob, setActiveJob] = useState<GitlabJob | null>(null);
  // Monotonic id for the in-flight trace request. Job/build switches can overlap
  // fetchTrace() calls; only the newest request is allowed to commit state so a
  // slower earlier response can't overwrite the log for the current selection.
  const traceReqSeq = useRef(0);

  // The Home feed doesn't carry the GitLab pipeline id/url (the /summary
  // endpoint omits them), so we resolve them from the build-detail response
  // below and fall back to whatever the caller did pass in.
  const [resolvedPid, setResolvedPid] = useState<string | null>(null);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);

  const headers = (() => { const t = localStorage.getItem('token'); return t ? { Authorization: `Bearer ${t}` } : undefined; })();

  // Load build detail (phases + reason) when a build is opened.
  useEffect(() => {
    setTab(0); setPhases(null); setReason(''); setErrorCode(null); setDetailError(null);
    setTrace(''); setTraceMeta(null); setTraceError(null); setTraceLoading(false);
    setJobs([]); setActiveJob(null);
    // Invalidate any trace request still in flight from the previous build so
    // its response can't land in this build's drawer.
    traceReqSeq.current++;
    // Seed with whatever the caller supplied; the detail fetch below upgrades these.
    setResolvedPid(build?.pipelineId ? String(build.pipelineId) : null);
    setResolvedUrl(build?.pipelineUrl || null);
    if (!build) return;
    let cancelled = false;
    setLoadingDetail(true);
    axios.get(`/api/builds/${encodeURIComponent(String(build.id))}`, { headers })
      .then(resp => {
        if (cancelled) return;
        const data = resp.data || {};
        const ph: Phase[] = Array.isArray(data.phases) ? data.phases : [];
        setPhases(ph);
        const failed = ph.find(p => /fail|cancel|error/.test((p.status || '').toLowerCase()) && p.errorMessage);
        setReason(failed?.errorMessage || data.failureReason || data.errorMessage || 'No detailed diagnostic message recorded for this build.');
        setErrorCode(failed?.errorCode || data.errorCode || null);
        // The build record carries the GitLab pipeline id/url that the feed
        // row lacked — wire them up so the log tab and "Open in GitLab" work.
        if (data.gitlabPipelineId) setResolvedPid(String(data.gitlabPipelineId));
        if (data.gitlabPipelineUrl) setResolvedUrl(String(data.gitlabPipelineUrl));
      })
      .catch(err => { if (!cancelled) setDetailError(err?.response?.data?.error || 'Failed to load build details'); })
      .finally(() => { if (!cancelled) setLoadingDetail(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [build?.id]);

  const fetchTrace = (jobId?: number) => {
    if (!build) return;
    const pid = resolvedPid ? String(resolvedPid) : '';
    if (!pid || pid === 'N/A' || pid.startsWith('NO_PIPELINE')) {
      setTraceError('No GitLab pipeline is associated with this build.');
      return;
    }
    setTraceLoading(true); setTraceError(null);
    const seq = ++traceReqSeq.current;
    const qs = jobId != null ? `?jobId=${encodeURIComponent(String(jobId))}` : '';
    axios.get(`/api/builds/gitlab/pipelines/${encodeURIComponent(pid)}/trace${qs}`, { headers })
      .then(resp => {
        if (traceReqSeq.current !== seq) return; // a newer request superseded this one
        setTrace(resp.data?.trace || '');
        setTraceMeta({ truncated: !!resp.data?.truncated });
        if (Array.isArray(resp.data?.jobs)) setJobs(resp.data.jobs);
        if (resp.data?.job) setActiveJob(resp.data.job);
      })
      .catch(err => { if (traceReqSeq.current === seq) setTraceError(err?.response?.data?.error || 'Failed to fetch GitLab job log'); })
      .finally(() => { if (traceReqSeq.current === seq) setTraceLoading(false); });
  };

  // Switch to a different job's trace (keeps the job list, swaps the log body).
  const selectJob = (jobId: number) => {
    if (activeJob?.id === jobId) return;
    setTrace(''); setTraceMeta(null); setTraceError(null);
    fetchTrace(jobId);
  };

  // Lazy-load the trace the first time the GitLab log tab is opened. Gate on
  // the detail fetch finishing so the pipeline id resolved from the build
  // record is available (the feed row alone doesn't carry it).
  useEffect(() => {
    if (tab === 1 && build && !loadingDetail && !trace && !traceLoading && !traceError) fetchTrace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, build?.id, loadingDetail, resolvedPid]);

  const isIOS = (build?.platform || '').toLowerCase() === 'ios';
  const meta = statusMeta(build?.status || '');
  const statusLc = (build?.status || '').toLowerCase();
  const isFailure = /fail|cancel/.test(statusLc);
  const isSuccess = /success/.test(statusLc);

  return (
    <Drawer
      anchor="right"
      open={!!build}
      onClose={onClose}
      slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: 'rgba(15, 23, 42, 0.3)' } } }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 640 }, borderRadius: 0 } }}
    >
      {build && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: isIOS ? '#0f172a' : '#3DDC84' }}>
                {isIOS ? <Apple sx={{ fontSize: 20 }} /> : <Android sx={{ fontSize: 20 }} />}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {build.appName || build.composerHost || 'Build'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  #{String(build.id)} · v{build.version || '—'} · {isIOS ? 'iOS' : (build.platform || '—')}
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Box>

          <Stack direction="row" spacing={1} sx={{ px: 3, pb: 1.5 }} alignItems="center" flexWrap="wrap">
            <Chip size="small" icon={meta.icon || undefined} label={meta.label} sx={{ fontWeight: 700, bgcolor: meta.bg, color: meta.color, '& .MuiChip-icon': { color: meta.color } }} />
            {build.submitter && <Chip size="small" label={build.submitter} sx={{ fontWeight: 600 }} />}
            {(resolvedUrl || build.pipelineUrl) && (
              <Button size="small" href={(resolvedUrl || build.pipelineUrl) as string} target="_blank" rel="noopener noreferrer" startIcon={<OpenInNew sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none', fontWeight: 700 }}>
                Open in GitLab
              </Button>
            )}
          </Stack>

          <Divider />
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, minHeight: 44 }}>
            <Tab icon={<BugReportOutlined sx={{ fontSize: 18 }} />} iconPosition="start" label="Reason & phases" sx={{ textTransform: 'none', fontWeight: 700, minHeight: 44 }} />
            <Tab icon={<OpenInNew sx={{ fontSize: 18 }} />} iconPosition="start" label="GitLab log" sx={{ textTransform: 'none', fontWeight: 700, minHeight: 44 }} />
          </Tabs>
          <Divider />

          <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
            {tab === 0 && (
              <>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                  {isFailure ? 'Core reason' : 'Result'}
                </Typography>
                {loadingDetail ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
                ) : detailError ? (
                  <Typography variant="body2" color="error">{detailError}</Typography>
                ) : (
                  <>
                    {isFailure ? (
                      // Only failed / cancelled builds have a diagnostic reason —
                      // show it in the red panel.
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(220, 38, 38, 0.06)', border: '1px solid', borderColor: 'rgba(220, 38, 38, 0.2)' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', color: '#7f1d1d' }}>
                          {reason}
                        </Typography>
                        {errorCode && <Chip size="small" label={`code: ${errorCode}`} sx={{ mt: 1, fontFamily: 'monospace', fontWeight: 700 }} />}
                      </Box>
                    ) : isSuccess ? (
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(22, 163, 74, 0.08)', border: '1px solid', borderColor: 'rgba(22, 163, 74, 0.25)' }}>
                        <Typography variant="body2" sx={{ color: '#166534', fontWeight: 600 }}>
                          This build completed successfully — no failure diagnostics were recorded.
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(59, 130, 246, 0.08)', border: '1px solid', borderColor: 'rgba(59, 130, 246, 0.25)' }}>
                        <Typography variant="body2" sx={{ color: '#1e40af', fontWeight: 600 }}>
                          This build is still in progress. If it fails, the reason will appear here.
                        </Typography>
                      </Box>
                    )}

                    {phases && phases.length > 0 && (
                      <>
                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mt: 2.5, mb: 1 }}>Phase timeline</Typography>
                        <Stack spacing={0.75}>
                          {phases.map((p, i) => (
                            <Stack key={i} direction="row" spacing={1.5} alignItems="center" sx={{ p: 1, borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: phaseColor(p.status), flexShrink: 0 }} />
                              <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'capitalize', flex: 1 }}>{(p.phase || '').replace(/_/g, ' ')}</Typography>
                              <Chip size="small" label={p.status} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                            </Stack>
                          ))}
                        </Stack>
                      </>
                    )}
                  </>
                )}
              </>
            )}

            {tab === 1 && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>GitLab job log</Typography>
                  {(trace || traceError) && !traceLoading && (
                    <Button size="small" onClick={() => { setTrace(''); setTraceMeta(null); setTraceError(null); fetchTrace(activeJob?.id); }} sx={{ textTransform: 'none' }}>Refresh</Button>
                  )}
                </Stack>

                {/* Per-job picker — each GitLab pipeline runs multiple jobs; let
                    the operator pick which one's trace to read. */}
                {jobs.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                      Job ({jobs.length})
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ rowGap: 0.75 }}>
                      {jobs.map(j => {
                        const selected = activeJob?.id === j.id;
                        return (
                          <Chip
                            key={j.id}
                            size="small"
                            clickable
                            onClick={() => selectJob(j.id)}
                            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: jobStatusColor(j.status), ml: 1 }} />}
                            label={`${j.name} · ${j.status}`}
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.68rem',
                              border: selected ? '1px solid #6366f1' : '1px solid #e2e8f0',
                              bgcolor: selected ? 'rgba(99, 102, 241, 0.12)' : 'transparent',
                              color: selected ? '#4338ca' : 'text.secondary',
                              '& .MuiChip-icon': { order: -1 },
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                )}

                {activeJob && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Showing: <span style={{ fontFamily: 'monospace' }}>{activeJob.name}</span>
                    </Typography>
                    {activeJob.web_url && (
                      <Button size="small" href={activeJob.web_url} target="_blank" rel="noopener noreferrer" startIcon={<OpenInNew sx={{ fontSize: 14 }} />} sx={{ textTransform: 'none', fontWeight: 700, py: 0, minWidth: 0 }}>
                        job in GitLab
                      </Button>
                    )}
                  </Stack>
                )}

                {traceLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                ) : traceError ? (
                  <Typography variant="body2" color="error">{traceError}</Typography>
                ) : trace ? (
                  <>
                    {traceMeta?.truncated && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Log truncated — open in GitLab for the full output.
                      </Typography>
                    )}
                    <Box component="pre" sx={{ m: 0, p: 1.5, borderRadius: 2, bgcolor: '#0f172a', color: '#e2e8f0', fontFamily: 'monospace', fontSize: '0.72rem', lineHeight: 1.5, overflowX: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: '100%' }}>
                      {trace}
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">No log output.</Typography>
                )}
              </>
            )}
          </Box>

          <Divider />
          <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={onClose} variant="outlined" color="inherit">Close</Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default BuildErrorDrawer;
